import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Spinner,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  EmptyState,
} from '@/components/ui';
import type { Source, IngestionJob } from '@/types';

export default function AdminPage() {
  const queryClient = useQueryClient();

  const { data: sources } = useQuery<Source[]>({
    queryKey: ['sources'],
    queryFn: () => api.getSources(),
  });

  const { data: jobs, isLoading: jobsLoading } = useQuery<IngestionJob[]>({
    queryKey: ['jobs'],
    queryFn: () => api.getJobs(),
  });

  const triggerJob = useMutation({
    mutationFn: () => api.triggerJob(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      window.alert('Job queued successfully');
    },
    onError: () => {
      window.alert('Failed to start job');
    },
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
      queued: 'default',
      running: 'info',
      completed: 'success',
      failed: 'danger',
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  if (jobsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
        <p className="text-gray-600">Manage data ingestion and system health</p>
      </div>

      <Card>
        <CardHeader><h3 className="font-semibold">Ingestion Jobs</h3></CardHeader>
        <CardBody>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                Click to queue a new ingestion job for processing
              </p>
            </div>
            <Button onClick={() => triggerJob.mutate()} isLoading={triggerJob.isPending}>
              Start Job
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex items-center justify-between">
          <h3 className="font-semibold">Ingestion Jobs</h3>
          <Button variant="outline" size="sm" onClick={() => queryClient.invalidateQueries({ queryKey: ['jobs'] })}>
            Refresh
          </Button>
        </CardHeader>
        <CardBody className="p-0">
          {jobs && jobs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-mono text-xs">{job.id.slice(0, 8)}...</TableCell>
                    <TableCell>{job.job_type}</TableCell>
                    <TableCell>{getStatusBadge(job.status)}</TableCell>
                    <TableCell>
                      {job.records_processed !== null ? (
                        <span className="text-sm">
                          {job.records_processed} processed
                          {job.records_created !== null && ` (${job.records_created} created)`}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(job.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyState icon="📋" title="No jobs yet" description="Start a job to see it here" />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader><h3 className="font-semibold">System Health</h3></CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-green-50 border border-green-200">
              <div className="text-2xl font-bold text-green-600">Healthy</div>
              <div className="text-sm text-green-600">Database</div>
            </div>
            <div className="p-4 rounded-lg bg-green-50 border border-green-200">
              <div className="text-2xl font-bold text-green-600">OK</div>
              <div className="text-sm text-green-600">API</div>
            </div>
            <div className="p-4 rounded-lg bg-green-50 border border-green-200">
              <div className="text-2xl font-bold text-green-600">
                {jobs?.filter((j) => j.status === 'failed').length || 0}
              </div>
              <div className="text-sm text-green-600">Failed Jobs</div>
            </div>
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
              <div className="text-2xl font-bold text-blue-600">
                {jobs?.filter((j) => j.status === 'running').length || 0}
              </div>
              <div className="text-sm text-blue-600">Running Jobs</div>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader><h3 className="font-semibold">Data Sources</h3></CardHeader>
        <CardBody className="p-0">
          {sources && sources.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>County</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Records</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.map((source) => (
                  <TableRow key={source.id}>
                    <TableCell>{source.name}</TableCell>
                    <TableCell><Badge variant="info">{source.source_type}</Badge></TableCell>
                    <TableCell>{source.county || '-'}</TableCell>
                    <TableCell>{source.state || '-'}</TableCell>
                    <TableCell>{source.record_count || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyState icon="📡" title="No sources" description="Add data sources to start ingesting records" />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
